import {
  resolveFromRegistry,
  type PrinterRegistry,
  type PrinterRole,
  type ResolvedPrinter,
} from "../../platform/printerRegistry";

/**
 * G-155 — what the two printer chips on "Print a note" say, as pure rules.
 *
 * Kept apart from the component so the rule is tested without React, the way
 * `itemStickerOutcome.ts` is for the sticker's words: a change to the
 * registry's fallback must flip a chip from "Same as ticket printer" to "Pair"
 * in a test, not on a counter.
 */

export type ChipState =
  | { kind: "paired"; printer: ResolvedPrinter }
  | { kind: "borrowed"; printer: ResolvedPrinter }
  | { kind: "empty" };

function other(role: PrinterRole): PrinterRole {
  return role === "receipts" ? "notes" : "receipts";
}

/** What the chip for `role` should show, worked out from one registry read. */
export function chipState(registry: PrinterRegistry, role: PrinterRole): ChipState {
  const resolved = resolveFromRegistry(registry, role);
  if (!resolved) return { kind: "empty" };
  return resolved.shared
    ? { kind: "borrowed", printer: resolved }
    : { kind: "paired", printer: resolved };
}

/**
 * Whether the OTHER role prints on this same machine — paired to it as well,
 * or borrowing it through the fallback. Either way the card should say that
 * swapping the roll is part of the job.
 */
export function sharesMachine(registry: PrinterRegistry, role: PrinterRole): boolean {
  const own = registry[role];
  if (!own) return false;
  const theirs = resolveFromRegistry(registry, other(role));
  return theirs?.printer.deviceId === own.deviceId;
}
